import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from '@bms2/shared';
import { Icon } from '../Icon';
import { AlertBanner } from './AlertBanner';
import styles from './SavedConfirmation.module.css';

/**
 * 🔒 The Saved Confirmation (piece 3; decision 1325): a save that succeeded
 * says so in a brief note at the screen's top-right corner, which fades after
 * four seconds. It floats over the page and never pushes it down, which is
 * what a green banner above the form did on every save.
 *
 * 🔒 The note is spoken through one polite live region that stays in the
 * document for the life of the page. A region created with its words already
 * inside is announced by some screen readers and not by others, so the region
 * is made once, empty, and each note is drawn into it.
 *
 * 🔒 Pointing at the note holds it: the timer and the fade both stop until the
 * pointer leaves, so a person who noticed it late can still read it.
 *
 * 🔨 A failure never confirms this way. The outcome notice below draws a
 * failure as the Alert Banner, which stays until it is dismissed.
 */

/** How long the note stands, its fade included; `--saved-confirmation-duration` in `styles/tokens.css` moves with it. */
export const SAVED_CONFIRMATION_MS = 4000;

/** `held` draws the note in place and still, for the Component Test Page; `live` raises the real one from a button. */
export const savedConfirmationDocumentedStates = ['held', 'live'] as const;

const HOST_ATTRIBUTE = 'data-saved-confirmation-host';

/** The one live region every note is drawn into, made on first use and never removed. */
function confirmationHost(): HTMLElement {
  const found = document.querySelector<HTMLElement>(`[${HOST_ATTRIBUTE}]`);
  if (found) return found;
  const host = document.createElement('div');
  host.setAttribute(HOST_ATTRIBUTE, 'true');
  host.setAttribute('role', 'status');
  host.setAttribute('aria-live', 'polite');
  host.setAttribute('aria-atomic', 'true');
  host.className = styles.host ?? '';
  document.body.appendChild(host);
  return host;
}

export interface SavedConfirmationProps {
  /** The words, through `t()`: what was saved, in a sentence. */
  message: string;
  /** Called once the note has stood its time; the caller clears what it was showing. */
  onDone: () => void;
  /** Harness only: draw in place, outside the live region, with no timer and no fade. */
  held?: boolean;
}

export function SavedConfirmation({ message, onDone, held = false }: SavedConfirmationProps) {
  const [paused, setPaused] = useState(false);
  // The application renders in the browser alone, so the region is found or made on the first render.
  const [host] = useState<HTMLElement | null>(() => (held ? null : confirmationHost()));
  const remaining = useRef(SAVED_CONFIRMATION_MS);
  const done = useRef(onDone);
  useEffect(() => {
    done.current = onDone;
  }, [onDone]);

  useEffect(() => {
    if (held || paused) return;
    const started = Date.now();
    const timer = window.setTimeout(() => done.current(), Math.max(0, remaining.current));
    return () => {
      window.clearTimeout(timer);
      remaining.current -= Date.now() - started;
    };
  }, [held, paused]);

  const note = (
    <div
      className={`${styles.note} ${held ? styles.held : ''}`}
      data-saved-confirmation={held ? 'held' : 'live'}
      data-paused={paused ? 'true' : 'false'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <span className={styles.icon}>
        <Icon meaning="bannerGood" size="inline" />
      </span>
      <span className={styles.message}>{message}</span>
    </div>
  );
  if (held) return note;
  return host ? createPortal(note, host) : null;
}

/** A save's outcome as the 51 hand-written notices hold it (T536): a tone and the words. */
export interface SaveOutcome {
  tone: 'good' | 'error';
  text: string;
}

/** Each outcome object gets its own number, so a second identical Saved restarts the note rather than extending the first. */
const outcomeIds = new WeakMap<SaveOutcome, number>();
let nextOutcomeId = 0;
function outcomeKey(outcome: SaveOutcome): number {
  let id = outcomeIds.get(outcome);
  if (id === undefined) {
    nextOutcomeId += 1;
    id = nextOutcomeId;
    outcomeIds.set(outcome, id);
  }
  return id;
}

/**
 * 🔒 The outcome notice (decision 1325): the one construction a save's result
 * draws through. A success is the Saved Confirmation, which clears itself once
 * it has stood its time; a failure is the Alert Banner with its words and a
 * Dismiss, which stays until the person closes it. Batch BN1 moves the
 * hand-written save notices onto it.
 */
export function OutcomeNotice({ outcome, onClear }: { outcome: SaveOutcome | null; onClear: () => void }) {
  if (!outcome) return null;
  if (outcome.tone === 'good') return <SavedConfirmation key={outcomeKey(outcome)} message={outcome.text} onDone={onClear} />;
  return <AlertBanner tone="error" message={outcome.text} action={{ label: t('modal.dismiss'), onAction: onClear }} />;
}
